import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Shield, 
  Swords, 
  Users, 
  MapPin, 
  Phone, 
  Calendar, 
  Check, 
  Menu, 
  X,
  Clock,
  Target,
  Dumbbell,
  ChevronRight,
  Mail,
  MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp Floating Button Component
const WhatsAppButton = () => {
  const phoneNumber = '447905440243'; // UK format without +
  const message = encodeURIComponent("Hi! I'm interested in booking a free trial class at U-TAC.");
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-button"
      className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#20BD5A] text-white p-4 rounded-full shadow-lg hover:scale-110 transition-all duration-300 flex items-center justify-center group"
      aria-label="Chat on WhatsApp"
    >
      <svg 
        viewBox="0 0 24 24" 
        className="w-7 h-7 fill-current"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="absolute right-full mr-3 bg-white text-gray-800 px-3 py-2 rounded-lg shadow-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        Chat with us!
      </span>
    </a>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'About', href: '#about' },
    { name: 'Classes', href: '#services' },
    { name: 'Timetable', href: '#timetable' },
    { name: 'Team', href: '#team' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/95 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="utac-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_bfd43db8-400b-405e-b1a5-8d1b78238fc9/artifacts/46wh0d3d_U-TAC%20MMA%20Logo_transparent.png"
              alt="U-TAC MMA Logo"
              className="h-14 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="nav-link text-gray-300 hover:text-white text-sm font-medium uppercase tracking-wider transition-colors"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <a href="https://u-tac.co.uk/timetable/" target="_blank" rel="noopener noreferrer">
              <Button 
                data-testid="nav-cta-button"
                className="bg-[#00D426] hover:bg-[#00B020] text-black font-bold uppercase tracking-wider px-6 py-2 rounded-none transition-all duration-300 hover:scale-105"
              >
                Book Free Trial
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        data-testid="mobile-menu"
        className={`mobile-menu fixed top-20 right-0 bottom-0 w-full bg-black/98 backdrop-blur-xl md:hidden ${isOpen ? 'open' : ''}`}
      >
        <div className="flex flex-col items-center justify-center h-full gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="text-white text-2xl font-bold uppercase tracking-wider font-['Oswald']"
              onClick={() => setIsOpen(false)}
            >
              {link.name}
            </a>
          ))}
          <a href="#contact" onClick={() => setIsOpen(false)}>
            <Button className="bg-[#00D426] hover:bg-[#00B020] text-black font-bold uppercase tracking-wider px-8 py-4 rounded-none mt-4">
              Book Free Trial
            </Button>
          </a>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-end pb-24 noise-overlay"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1737381508529-a110d717d5a7?crop=entropy&cs=srgb&fm=jpg&q=85')`
        }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />
      
      <div className="utac-container relative z-10">
        <div className="max-w-3xl">
          <p className="text-[#00D426] text-sm font-medium uppercase tracking-[0.3em] mb-4 animate-fade-in-up">
            Stafford's Premier Combat Training Facility
          </p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase tracking-tighter leading-none mb-6 font-['Oswald'] animate-fade-in-up animation-delay-100">
            A Place of<br />Investigation
          </h1>
          <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8 max-w-xl animate-fade-in-up animation-delay-200">
            Scientific Reality-Based Combat & Self Defence. Over 20 years of training, 
            real-life experiences, and constantly asking: "Does it really work?"
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
            <a href="https://u-tac.co.uk/timetable/" target="_blank" rel="noopener noreferrer">
              <Button 
                data-testid="hero-cta-button"
                className="btn-primary bg-[#00D426] hover:bg-[#00B020] text-black font-bold uppercase tracking-wider px-8 py-6 rounded-none text-lg animate-pulse-glow"
              >
                Book Your Free Trial
                <ChevronRight className="ml-2" size={20} />
              </Button>
            </a>
            <a href="#timetable">
              <Button 
                data-testid="hero-secondary-button"
                variant="outline"
                className="bg-transparent border-2 border-white/30 hover:border-white text-white font-bold uppercase tracking-wider px-8 py-6 rounded-none text-lg hover:bg-white/5"
              >
                View Timetable
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden md:block">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-[#00D426] rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const benefits = [
    'Reality-based combat system developed over 20+ years',
    'Constantly evolving techniques and philosophies',
    'Expert instructors with real-world experience',
    'Welcoming environment for all skill levels',
    'Part of a global network of self-defence facilities'
  ];

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-[#050505] noise-overlay">
      <div className="utac-container">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div>
            <p className="text-[#00D426] text-xs font-medium uppercase tracking-[0.3em] mb-4">
              About U-TAC
            </p>
            <h2 className="section-title text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-8 font-['Oswald']">
              Always Evolving.<br />Always Testing.
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
              U-TAC is "a place of investigation" — the scientific study of physical 
              and mental aspects of reality-based combat and self-defence.
            </p>
            <p className="text-gray-400 leading-relaxed mb-8">
              The ever-developing U-TAC Self-Defence System is constantly adjusting 
              and EVOLVING with its surroundings. Techniques, principles and philosophies 
              are always tested to answer the question "Does it Work?"
            </p>
            <p className="text-gray-500 italic">
              We never believe that it's "perfect"... Always watching, listening, 
              training, testing and ultimately EVOLVING.
            </p>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] bg-neutral-900 overflow-hidden">
              <img 
                src="https://u-tac.co.uk/wp-content/uploads/sites/972/2025/10/IMG_20250702_202645-1024x768.jpg"
                alt="U-TAC Training"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Stats overlay */}
            <div className="absolute -bottom-8 -left-8 bg-[#00D426] p-6 md:p-8">
              <div className="text-white">
                <p className="text-4xl md:text-5xl font-bold font-['Oswald']">20+</p>
                <p className="text-sm uppercase tracking-wider mt-1">Years Experience</p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-24 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-start gap-4 p-6 bg-neutral-900/50 border border-white/5">
              <Check className="text-[#00D426] flex-shrink-0 mt-1" size={20} />
              <p className="text-gray-300">{benefit}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section (Bento Grid)
const ServicesSection = () => {
  const services = [
    {
      title: 'MMA & Self Defence',
      description: 'Reality-based combat system for real-world situations',
      icon: Shield,
      image: 'https://u-tac.co.uk/wp-content/uploads/sites/972/2025/10/IMG_20250702_202645-1024x768.jpg',
      gridClass: 'col-span-2 row-span-2'
    },
    {
      title: 'Thai Boxing',
      description: 'Authentic Muay Thai training with expert instruction',
      icon: Swords,
      image: 'https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?crop=entropy&cs=srgb&fm=jpg&q=85',
      gridClass: 'col-span-2 row-span-1'
    },
    {
      title: 'Personal Training',
      description: 'Semi-private fitness sessions tailored to your goals',
      icon: Dumbbell,
      image: 'https://images.unsplash.com/photo-1770513649465-2c60c8039806?crop=entropy&cs=srgb&fm=jpg&q=85',
      gridClass: 'col-span-1 row-span-1'
    },
    {
      title: 'Kids Classes',
      description: 'Super Sparks (5-7) & Street Smarts (8-13)',
      icon: Users,
      image: 'https://u-tac.co.uk/wp-content/uploads/sites/972/2025/10/IMG_20251019_105238-1024x768.jpg',
      gridClass: 'col-span-1 row-span-1'
    }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="utac-container">
        <div className="text-center mb-16">
          <p className="text-[#00D426] text-xs font-medium uppercase tracking-[0.3em] mb-4">
            Our Classes
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-white uppercase tracking-tight font-['Oswald']">
            Train Like a Fighter
          </h2>
        </div>

        <div className="bento-grid">
          {services.map((service, index) => (
            <div 
              key={index}
              data-testid={`service-card-${index}`}
              className={`service-card group relative overflow-hidden bg-neutral-900 border border-white/5 hover:border-[#00D426]/50 transition-all duration-500 ${service.gridClass}`}
            >
              <img 
                src={service.image}
                alt={service.title}
                className="service-image absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
                <service.icon className="text-[#00D426] mb-4" size={32} />
                <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-wide font-['Oswald'] mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-400 text-sm">{service.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Timetable Section
const TimetableSection = () => {
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTimetable = async () => {
      try {
        const response = await axios.get(`${API}/timetable`);
        setTimetable(response.data);
      } catch (error) {
        console.error('Error fetching timetable:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTimetable();
  }, []);

  const adultClasses = timetable.filter(t => t.category === 'adults');
  const kidsClasses = timetable.filter(t => t.category === 'kids');

  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  const sortByDay = (classes) => {
    return [...classes].sort((a, b) => {
      const dayDiff = dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
      if (dayDiff !== 0) return dayDiff;
      return a.time.localeCompare(b.time);
    });
  };

  const TimetableTable = ({ classes }) => (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="timetable-table">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-gray-500 font-medium">Day</th>
            <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-gray-500 font-medium">Time</th>
            <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-gray-500 font-medium">Class</th>
            <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-gray-500 font-medium">Age Group</th>
          </tr>
        </thead>
        <tbody>
          {sortByDay(classes).map((item, index) => (
            <tr 
              key={index} 
              className="timetable-row border-b border-white/5 transition-colors"
              data-testid={`timetable-row-${index}`}
            >
              <td className="py-4 px-4 text-white font-medium">{item.day}</td>
              <td className="py-4 px-4 text-[#00D426] font-mono">{item.time}</td>
              <td className="py-4 px-4 text-white">{item.class_name}</td>
              <td className="py-4 px-4 text-gray-400">{item.age_group}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <section id="timetable" data-testid="timetable-section" className="py-24 md:py-32 bg-[#050505]">
      <div className="utac-container">
        <div className="text-center mb-16">
          <p className="text-[#00D426] text-xs font-medium uppercase tracking-[0.3em] mb-4">
            Class Schedule
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-white uppercase tracking-tight font-['Oswald']">
            Weekly Timetable
          </h2>
        </div>

        {loading ? (
          <div className="text-center text-gray-400">Loading timetable...</div>
        ) : (
          <Tabs defaultValue="adults" className="w-full">
            <TabsList className="w-full max-w-md mx-auto grid grid-cols-2 bg-neutral-900 rounded-none p-1 mb-8">
              <TabsTrigger 
                value="adults" 
                data-testid="tab-adults"
                className="rounded-none data-[state=active]:bg-[#00D426] data-[state=active]:text-white uppercase tracking-wider font-bold"
              >
                Adults
              </TabsTrigger>
              <TabsTrigger 
                value="kids"
                data-testid="tab-kids"
                className="rounded-none data-[state=active]:bg-[#00D426] data-[state=active]:text-white uppercase tracking-wider font-bold"
              >
                Kids
              </TabsTrigger>
            </TabsList>
            <TabsContent value="adults" className="bg-neutral-900/50 border border-white/5 p-4 md:p-6">
              <TimetableTable classes={adultClasses} />
            </TabsContent>
            <TabsContent value="kids" className="bg-neutral-900/50 border border-white/5 p-4 md:p-6">
              <TimetableTable classes={kidsClasses} />
            </TabsContent>
          </Tabs>
        )}

        <div className="text-center mt-8">
          <a href="https://u-tac.co.uk/timetable/" target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="timetable-cta-button"
              className="bg-[#00D426] hover:bg-[#00B020] text-black font-bold uppercase tracking-wider px-8 py-4 rounded-none"
            >
              Book Your Free Trial
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'Dan Delgyln',
      text: "Been doing this martial art for a few weeks now and I have to say it's the best one that I've ever tried. The instructors Jay and Daz are brilliant, the atmosphere is super friendly and everyone busts a gut with a big smile on their face. Don't delay sign up today and find the new you!"
    },
    {
      name: 'Dave Stephenson',
      text: "This is a super accessible programme if you've never entertained the idea of doing self defence or martial arts before. Jay is a great teacher, it's a friendly environment to train in and the moves look pretty sweet!"
    },
    {
      name: 'Dave Butler',
      text: "I have always wanted to try some mixed martial arts and learn how to self defence. Jay and Darren were great with everyone who came and really helpful. Highly recommend going."
    }
  ];

  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="utac-container">
        <div className="text-center mb-16">
          <p className="text-[#00D426] text-xs font-medium uppercase tracking-[0.3em] mb-4">
            Testimonials
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-white uppercase tracking-tight font-['Oswald']">
            What People Say
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              data-testid={`testimonial-${index}`}
              className="testimonial-card bg-neutral-900/50 p-8 border-l-4 border-[#00D426]"
            >
              <p className="text-gray-300 leading-relaxed mb-6 relative z-10">
                "{testimonial.text}"
              </p>
              <p className="text-white font-bold uppercase tracking-wider font-['Oswald']">
                {testimonial.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  const team = [
    {
      name: 'Jay Savery',
      role: 'Lead Instructor / Owner',
      image: 'https://u-tac.co.uk/wp-content/uploads/sites/972/2020/10/Jay-scaled.jpg'
    },
    {
      name: 'Brad Brookes',
      role: 'Thai Boxing Instructor',
      image: 'https://u-tac.co.uk/wp-content/uploads/sites/972/2020/10/Brad-scaled-e1603717870709.jpg'
    },
    {
      name: 'Vicky Howard',
      role: 'Super Sparks Instructor',
      image: 'https://u-tac.co.uk/wp-content/uploads/sites/972/2020/10/Vicky-scaled-e1603717970650.jpg'
    }
  ];

  return (
    <section id="team" data-testid="team-section" className="py-24 md:py-32 bg-[#050505]">
      <div className="utac-container">
        <div className="text-center mb-16">
          <p className="text-[#00D426] text-xs font-medium uppercase tracking-[0.3em] mb-4">
            Our Team
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-white uppercase tracking-tight font-['Oswald']">
            Meet The Instructors
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {team.map((member, index) => (
            <div 
              key={index}
              data-testid={`team-member-${index}`}
              className="team-card relative aspect-[3/4] bg-neutral-900 overflow-hidden group"
            >
              <img 
                src={member.image}
                alt={member.name}
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
              />
              <div className="team-overlay absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-xl font-bold text-white uppercase tracking-wide font-['Oswald']">
                  {member.name}
                </h3>
                <p className="text-[#00D426] text-sm uppercase tracking-wider">{member.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Banner
const CTABanner = () => {
  return (
    <section data-testid="cta-banner" className="py-16 md:py-24 bg-[#00D426] cta-banner relative overflow-hidden">
      <div className="utac-container relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl md:text-5xl font-bold text-white uppercase tracking-tight font-['Oswald'] mb-2">
              Start Your Journey Today
            </h2>
            <p className="text-white/80 text-lg">
              Claim your FREE trial class and discover the U-TAC difference
            </p>
          </div>
          <a href="https://u-tac.co.uk/timetable/" target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="banner-cta-button"
              className="bg-white text-[#00D426] hover:bg-gray-100 font-bold uppercase tracking-wider px-8 py-6 rounded-none text-lg whitespace-nowrap"
            >
              Book Free Trial
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section with Forms
const ContactSection = () => {
  const [leadForm, setLeadForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });

  const [callbackForm, setCallbackForm] = useState({
    name: '',
    phone: '',
    preferred_time: '',
    interest: ''
  });

  const [leadLoading, setLeadLoading] = useState(false);
  const [callbackLoading, setCallbackLoading] = useState(false);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadLoading(true);
    try {
      await axios.post(`${API}/leads`, leadForm);
      toast.success('Thank you! We\'ll be in touch soon.');
      setLeadForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        interest: '',
        message: ''
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Error submitting lead:', error);
    } finally {
      setLeadLoading(false);
    }
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    setCallbackLoading(true);
    try {
      await axios.post(`${API}/callback-requests`, callbackForm);
      toast.success('Callback requested! We\'ll call you soon.');
      setCallbackForm({
        name: '',
        phone: '',
        preferred_time: '',
        interest: ''
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Error submitting callback:', error);
    } finally {
      setCallbackLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="utac-container">
        <div className="text-center mb-16">
          <p className="text-[#00D426] text-xs font-medium uppercase tracking-[0.3em] mb-4">
            Get Started
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-white uppercase tracking-tight font-['Oswald']">
            Book Your Free Trial
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Ready to start your martial arts journey? Fill in the form below or request a callback and we'll get you booked in for your free trial class.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="form-container p-8 md:p-10 border border-white/5">
            <h3 className="text-2xl font-bold text-white uppercase tracking-wide font-['Oswald'] mb-6 flex items-center gap-3">
              <Mail className="text-[#00D426]" size={24} />
              Contact Form
            </h3>
            <form onSubmit={handleLeadSubmit} data-testid="lead-form">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-400 mb-2 block">
                    First Name *
                  </Label>
                  <Input
                    data-testid="lead-first-name"
                    required
                    value={leadForm.first_name}
                    onChange={(e) => setLeadForm({...leadForm, first_name: e.target.value})}
                    className="bg-neutral-900 border-neutral-800 focus:border-[#00D426] text-white rounded-none h-12"
                    placeholder="John"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-400 mb-2 block">
                    Last Name *
                  </Label>
                  <Input
                    data-testid="lead-last-name"
                    required
                    value={leadForm.last_name}
                    onChange={(e) => setLeadForm({...leadForm, last_name: e.target.value})}
                    className="bg-neutral-900 border-neutral-800 focus:border-[#00D426] text-white rounded-none h-12"
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="mb-4">
                <Label className="text-xs uppercase tracking-widest text-neutral-400 mb-2 block">
                  Email *
                </Label>
                <Input
                  data-testid="lead-email"
                  type="email"
                  required
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                  className="bg-neutral-900 border-neutral-800 focus:border-[#00D426] text-white rounded-none h-12"
                  placeholder="john@example.com"
                />
              </div>
              <div className="mb-4">
                <Label className="text-xs uppercase tracking-widest text-neutral-400 mb-2 block">
                  Phone *
                </Label>
                <Input
                  data-testid="lead-phone"
                  type="tel"
                  required
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                  className="bg-neutral-900 border-neutral-800 focus:border-[#00D426] text-white rounded-none h-12"
                  placeholder="07123 456789"
                />
              </div>
              <div className="mb-4">
                <Label className="text-xs uppercase tracking-widest text-neutral-400 mb-2 block">
                  I'm Interested In *
                </Label>
                <Select 
                  value={leadForm.interest} 
                  onValueChange={(value) => setLeadForm({...leadForm, interest: value})}
                  required
                >
                  <SelectTrigger 
                    data-testid="lead-interest"
                    className="bg-neutral-900 border-neutral-800 focus:border-[#00D426] text-white rounded-none h-12"
                  >
                    <SelectValue placeholder="Select your interest" />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-900 border-neutral-800">
                    <SelectItem value="martial_arts">Martial Arts / Self Defence</SelectItem>
                    <SelectItem value="personal_training">Personal Training</SelectItem>
                    <SelectItem value="kids_classes">Kids Classes</SelectItem>
                    <SelectItem value="thai_boxing">Thai Boxing</SelectItem>
                    <SelectItem value="general">General Enquiry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mb-6">
                <Label className="text-xs uppercase tracking-widest text-neutral-400 mb-2 block">
                  Message (Optional)
                </Label>
                <Textarea
                  data-testid="lead-message"
                  value={leadForm.message}
                  onChange={(e) => setLeadForm({...leadForm, message: e.target.value})}
                  className="bg-neutral-900 border-neutral-800 focus:border-[#00D426] text-white rounded-none min-h-[100px]"
                  placeholder="Tell us more about your goals..."
                />
              </div>
              <Button
                type="submit"
                data-testid="lead-submit-button"
                disabled={leadLoading || !leadForm.interest}
                className="w-full bg-[#00D426] hover:bg-[#00B020] text-white font-bold uppercase tracking-wider py-4 rounded-none disabled:opacity-50"
              >
                {leadLoading ? 'Sending...' : 'Book Your Free Trial'}
              </Button>
            </form>
          </div>

          {/* Callback Request Form */}
          <div className="space-y-8">
            <div className="form-container p-8 md:p-10 border border-white/5">
              <h3 className="text-2xl font-bold text-white uppercase tracking-wide font-['Oswald'] mb-6 flex items-center gap-3">
                <Phone className="text-[#00D426]" size={24} />
                Request Callback
              </h3>
              <form onSubmit={handleCallbackSubmit} data-testid="callback-form">
                <div className="mb-4">
                  <Label className="text-xs uppercase tracking-widest text-neutral-400 mb-2 block">
                    Your Name *
                  </Label>
                  <Input
                    data-testid="callback-name"
                    required
                    value={callbackForm.name}
                    onChange={(e) => setCallbackForm({...callbackForm, name: e.target.value})}
                    className="bg-neutral-900 border-neutral-800 focus:border-[#00D426] text-white rounded-none h-12"
                    placeholder="Your full name"
                  />
                </div>
                <div className="mb-4">
                  <Label className="text-xs uppercase tracking-widest text-neutral-400 mb-2 block">
                    Phone Number *
                  </Label>
                  <Input
                    data-testid="callback-phone"
                    type="tel"
                    required
                    value={callbackForm.phone}
                    onChange={(e) => setCallbackForm({...callbackForm, phone: e.target.value})}
                    className="bg-neutral-900 border-neutral-800 focus:border-[#00D426] text-white rounded-none h-12"
                    placeholder="07123 456789"
                  />
                </div>
                <div className="mb-4">
                  <Label className="text-xs uppercase tracking-widest text-neutral-400 mb-2 block">
                    Best Time to Call *
                  </Label>
                  <Select 
                    value={callbackForm.preferred_time} 
                    onValueChange={(value) => setCallbackForm({...callbackForm, preferred_time: value})}
                    required
                  >
                    <SelectTrigger 
                      data-testid="callback-time"
                      className="bg-neutral-900 border-neutral-800 focus:border-[#00D426] text-white rounded-none h-12"
                    >
                      <SelectValue placeholder="Select preferred time" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800">
                      <SelectItem value="morning">Morning (9am - 12pm)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                      <SelectItem value="evening">Evening (5pm - 8pm)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="mb-6">
                  <Label className="text-xs uppercase tracking-widest text-neutral-400 mb-2 block">
                    I'm Interested In *
                  </Label>
                  <Select 
                    value={callbackForm.interest} 
                    onValueChange={(value) => setCallbackForm({...callbackForm, interest: value})}
                    required
                  >
                    <SelectTrigger 
                      data-testid="callback-interest"
                      className="bg-neutral-900 border-neutral-800 focus:border-[#00D426] text-white rounded-none h-12"
                    >
                      <SelectValue placeholder="Select your interest" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800">
                      <SelectItem value="martial_arts">Martial Arts / Self Defence</SelectItem>
                      <SelectItem value="personal_training">Personal Training</SelectItem>
                      <SelectItem value="kids_classes">Kids Classes</SelectItem>
                      <SelectItem value="thai_boxing">Thai Boxing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  data-testid="callback-submit-button"
                  disabled={callbackLoading || !callbackForm.preferred_time || !callbackForm.interest}
                  className="w-full bg-transparent border-2 border-[#00D426] text-[#00D426] hover:bg-[#00D426] hover:text-white font-bold uppercase tracking-wider py-4 rounded-none disabled:opacity-50 transition-all"
                >
                  {callbackLoading ? 'Requesting...' : 'Request Callback'}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="bg-neutral-900/50 p-8 border border-white/5">
              <h4 className="text-lg font-bold text-white uppercase tracking-wide font-['Oswald'] mb-6">
                Visit Us
              </h4>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="text-[#00D426] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-white font-medium">U-TAC Mixed Martial Arts & Box Gym</p>
                    <p className="text-gray-400">Romford Rd, Stafford, ST16 3DZ</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Phone className="text-[#00D426] flex-shrink-0" size={20} />
                  <a href="tel:07905440243" className="text-white hover:text-[#00D426] transition-colors">
                    07905 440243
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-12 bg-[#050505] border-t border-white/5">
      <div className="utac-container">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Logo & About */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_bfd43db8-400b-405e-b1a5-8d1b78238fc9/artifacts/46wh0d3d_U-TAC%20MMA%20Logo_transparent.png"
                alt="U-TAC MMA Logo"
                className="h-16 w-auto"
              />
            </div>
            <p className="text-gray-400 mb-4">
              A place of investigation — scientific reality-based combat and self-defence training in Stafford.
            </p>
            <p className="text-gray-500 text-sm">
              Part of a global network of self-defence and fitness facilities.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider font-['Oswald'] mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {['About', 'Classes', 'Timetable', 'Team', 'Contact'].map((link) => (
                <li key={link}>
                  <a 
                    href={`#${link.toLowerCase()}`}
                    className="footer-link text-gray-400 hover:text-[#00D426] transition-all"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider font-['Oswald'] mb-4">
              Contact
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin size={18} className="text-[#00D426] flex-shrink-0 mt-1" />
                <span>Romford Rd, Stafford, ST16 3DZ</span>
              </li>
              <li>
                <a href="tel:07905440243" className="flex items-center gap-3 text-gray-400 hover:text-[#00D426] transition-colors">
                  <Phone size={18} className="text-[#00D426]" />
                  07905 440243
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} U-TAC Mixed Martial Arts & Box Gym. All rights reserved.
          </p>
          <p className="text-gray-600 text-sm">
            Stafford's Premier Combat Training Facility
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <main data-testid="landing-page">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <TimetableSection />
      <TestimonialsSection />
      <TeamSection />
      <CTABanner />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </main>
  );
};

export default LandingPage;
